import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { Box, Dialog, DialogContent, Grid } from "@mui/material";
import Typography from "@mui/material/Typography";
import { SubmitHandler, useForm } from "react-hook-form";
import CancelButton from "components/button/CancelButton";
import { UPLOAD_ACCEPT } from "constants/upload";
import MultipleImageUpload from "./MultipleImageUpload";
import { TUploadImageType, WheelBrandListItem, WheelItemPrice } from "../types/wheel-brand";

// Constants
const HOLE_LABELS = ["4穴", "5穴", "6穴"] as const;
const COLORS = ["silver", "black", "white"] as const;
const COLOR_LABELS = {
  silver: "シルバー",
  black: "ブラック",
  white: "ホワイト",
} as const;

// Types
type FormData = {
  silver_0?: File | null;
  silver_1?: File | null;
  silver_2?: File | null;
  black_0?: File | null;
  black_1?: File | null;
  black_2?: File | null;
  white_0?: File | null;
  white_1?: File | null;
  white_2?: File | null;
};

type ColorType = (typeof COLORS)[number];

interface WheelImagesDetailModalProps {
  showWheelImgModal: boolean;
  handleClose: () => void;
  wheelItemPriceSelected: WheelBrandListItem;
  onDeleteImage: (type: TUploadImageType, color?: string, hole?: string) => Promise<void>;
  onUploadImage: (
    type: TUploadImageType,
    file: File,
    color?: string,
    hole?: string,
  ) => Promise<void>;
  accept?: string;
  onHandleSubmit?: (data: { silver: File[]; black: File[]; white: File[] }) => void;
}

// Styles
const styles = {
  label: {
    fontSize: { xs: "20px", md: "24px", lg: "33px" },
    fontWeight: "medium",
    color: "var(--gray-600)",
  },
  dialogTitle: {
    fontWeight: "fontWeightMedium",
    color: "var(--gray-600)",
    fontSize: { xs: 28, md: 33 },
  },
  holeLabel: {
    width: { xs: "calc((100% - 16px)/3)", md: "calc((100% - 32px)/3)" },
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  dialogContent: {
    px: { xs: 4, md: 6 },
    pb: { xs: 4, md: 6 },
  },
  colorRow: {
    alignItems: "center",
  },
  colorRowWithSpacing: {
    alignItems: "center",
    py: 5,
  },
} as const;

// Helper function to convert URL to File object for display
const createFileFromUrl = (url: string | null, fileName: string): File | null =>
  url
    ? (() => {
        const dummyFile = new File([], fileName, { type: "image/jpeg" });
        (dummyFile as any).previewUrl = url;
        return dummyFile;
      })()
    : null;

// Helper function to map wheel image URLs to FormData
const mapWheelImageUrlsToFormData = (wheelItemPrice: WheelItemPrice): FormData => ({
  silver_0: createFileFromUrl(wheelItemPrice.wheel_image_url_silver_4, "silver_4.jpg"),
  silver_1: createFileFromUrl(wheelItemPrice.wheel_image_url_silver_5, "silver_5.jpg"),
  silver_2: createFileFromUrl(wheelItemPrice.wheel_image_url_silver_6, "silver_6.jpg"),
  black_0: createFileFromUrl(wheelItemPrice.wheel_image_url_black_4, "black_4.jpg"),
  black_1: createFileFromUrl(wheelItemPrice.wheel_image_url_black_5, "black_5.jpg"),
  black_2: createFileFromUrl(wheelItemPrice.wheel_image_url_black_6, "black_6.jpg"),
  white_0: createFileFromUrl(wheelItemPrice.wheel_image_url_white_4, "white_4.jpg"),
  white_1: createFileFromUrl(wheelItemPrice.wheel_image_url_white_5, "white_5.jpg"),
  white_2: createFileFromUrl(wheelItemPrice.wheel_image_url_white_6, "white_6.jpg"),
});

// Custom hook for form logic
const useWheelImageForm = (wheelItemPrice?: WheelItemPrice) => {
  const defaultValues = useMemo(
    () => (wheelItemPrice ? mapWheelImageUrlsToFormData(wheelItemPrice) : {}),
    [wheelItemPrice],
  );

  const { control, handleSubmit, watch, reset } = useForm<FormData>({
    defaultValues,
  });

  const watchedValues = watch();

  const hasFilesByColor = useMemo(
    () =>
      COLORS.reduce(
        (acc, color) => {
          const fields = [`${color}_0`, `${color}_1`, `${color}_2`] as const;
          acc[color] = fields.some((field) => watchedValues[field]);
          return acc;
        },
        {} as Record<ColorType, boolean>,
      ),
    [watchedValues],
  );

  const onSubmit: SubmitHandler<FormData> = useCallback(
    (data) =>
      COLORS.reduce(
        (acc, color) => {
          const fields = [`${color}_0`, `${color}_1`, `${color}_2`] as const;
          acc[color] = fields.map((field) => data[field]).filter(Boolean) as File[];
          return acc;
        },
        {} as { silver: File[]; black: File[]; white: File[] },
      ),
    [],
  );

  // Reset form when wheelItemPrice changes
  // Reset form when wheelItemPrice changes
  useEffect(() => {
    if (wheelItemPrice) {
      Promise.resolve().then(() => {
        const newDefaultValues = mapWheelImageUrlsToFormData(wheelItemPrice);
        reset(newDefaultValues);
      });
    }
  }, [wheelItemPrice, reset]);

  return {
    control,
    handleSubmit,
    hasFilesByColor,
    onSubmit,
  };
};

// Memoized components
const HoleLabels = memo(() => (
  <Grid container alignItems="center" mb={1.5}>
    <Grid item xs={12} md={3} />
    <Grid item xs={12} md={9} sx={{ display: "flex", gap: { xs: 1, md: 2 } }}>
      {HOLE_LABELS.map((label) => (
        <Box
          key={label}
          sx={{
            ...styles.holeLabel,
            ...styles.label,
          }}
        >
          {label}
        </Box>
      ))}
    </Grid>
  </Grid>
));

HoleLabels.displayName = "HoleLabels";

const DialogHeader = memo<{
  onClose: () => void;
}>(({ onClose }) => (
  <Box display="flex" justifyContent="space-between" alignItems="center" p={3} pb={0}>
    <Typography variant="h4" component="h2" sx={styles.dialogTitle}>
      詳細画像アップ
    </Typography>
    <CancelButton sx={{ color: "var(--gray-600)" }} onClose={onClose} />
  </Box>
));

DialogHeader.displayName = "DialogHeader";

/**
 * WheelImagesDetailModal Component
 *
 * A modal component for uploading detailed wheel images organized by color and hole count.
 * Supports drag-and-drop image upload with automatic API calls.
 * @param props - Component props
 * @param props.showWheelImgModal - Controls modal visibility
 * @param props.setShowWheelImgModal - Callback to toggle modal
 * @param props.onHandleSubmit - Callback when form is submitted
 * @param props.defaultValues - Initial form values
 * @param props.wheelItemPriceSelected - Selected wheel item data
 */
const WheelImagesDetailModal = memo<WheelImagesDetailModalProps>(
  ({
    showWheelImgModal,
    handleClose,
    onHandleSubmit,
    wheelItemPriceSelected,
    onDeleteImage,
    onUploadImage,
    accept = UPLOAD_ACCEPT.IMAGE.MIME_TYPES,
  }) => {
    const [imageCacheBuster, setImageCacheBuster] = useState<number>(Date.now());

    const { control, handleSubmit, hasFilesByColor, onSubmit } =
      useWheelImageForm(wheelItemPriceSelected);

    // Wrapper function to update cache buster after successful upload
    const handleUploadImageWithCacheBuster = useCallback(
      async (type: TUploadImageType, file: File, color?: string, hole?: string) => {
        await onUploadImage(type, file, color, hole);
        // Update cache buster after successful upload to force image reload
        setImageCacheBuster(Date.now());
      },
      [onUploadImage],
    );

    const handleFormSubmit = useCallback(
      (data: FormData) => {
        const groupedData = onSubmit(data);
        handleClose();
      },
      [onSubmit, onHandleSubmit, handleClose],
    );

    const itemCd = useMemo(
      () => wheelItemPriceSelected.brand_cd,
      [wheelItemPriceSelected.brand_cd],
    );

    // Reset cache buster when modal opens or wheelItemPriceSelected changes
    useEffect(() => {
      if (showWheelImgModal && wheelItemPriceSelected) {
        setImageCacheBuster(Date.now());
      }
    }, [showWheelImgModal, wheelItemPriceSelected]);

    // Update cache buster when any image URL changes
    useEffect(() => {
      if (wheelItemPriceSelected) {
        const imageUrls = [
          wheelItemPriceSelected.wheel_image_url_silver_4,
          wheelItemPriceSelected.wheel_image_url_silver_5,
          wheelItemPriceSelected.wheel_image_url_silver_6,
          wheelItemPriceSelected.wheel_image_url_black_4,
          wheelItemPriceSelected.wheel_image_url_black_5,
          wheelItemPriceSelected.wheel_image_url_black_6,
          wheelItemPriceSelected.wheel_image_url_white_4,
          wheelItemPriceSelected.wheel_image_url_white_5,
          wheelItemPriceSelected.wheel_image_url_white_6,
        ];
        // Update cache buster when any image URL changes
        setImageCacheBuster(Date.now());
      }
    }, [
      wheelItemPriceSelected?.wheel_image_url_silver_4,
      wheelItemPriceSelected?.wheel_image_url_silver_5,
      wheelItemPriceSelected?.wheel_image_url_silver_6,
      wheelItemPriceSelected?.wheel_image_url_black_4,
      wheelItemPriceSelected?.wheel_image_url_black_5,
      wheelItemPriceSelected?.wheel_image_url_black_6,
      wheelItemPriceSelected?.wheel_image_url_white_4,
      wheelItemPriceSelected?.wheel_image_url_white_5,
      wheelItemPriceSelected?.wheel_image_url_white_6,
    ]);

    return (
      <Dialog
        open={showWheelImgModal}
        onClose={handleClose}
        maxWidth="md"
        PaperProps={{
          sx: { borderRadius: "21px" },
        }}
        fullWidth
      >
        <DialogHeader onClose={handleClose} />

        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <DialogContent sx={styles.dialogContent}>
            <HoleLabels />

            {COLORS.map((color) => (
              <Grid
                key={color}
                container
                sx={color === "black" ? styles.colorRowWithSpacing : styles.colorRow}
              >
                <Grid item xs={12} md={3} sx={{ pb: hasFilesByColor[color] ? 6 : 0 }}>
                  <Typography sx={styles.label}>{COLOR_LABELS[color]}</Typography>
                </Grid>
                <Grid item xs={12} md={9}>
                  <MultipleImageUpload
                    baseFieldName={color}
                    control={control}
                    maxImages={3}
                    accept={accept}
                    itemCd={itemCd}
                    color={color}
                    cacheBuster={imageCacheBuster}
                    onDeleteImage={onDeleteImage}
                    onUploadImage={handleUploadImageWithCacheBuster}
                  />
                </Grid>
              </Grid>
            ))}
          </DialogContent>
        </form>
      </Dialog>
    );
  },
);

WheelImagesDetailModal.displayName = "WheelImagesDetailModal";

export default WheelImagesDetailModal;
